import React, { useEffect, useMemo, useState } from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import { motion } from "framer-motion";
import image from '../images/nukeproof-gravel.jpg';
import kolo from '../images/bike.png';
import { NavLink, useParams } from 'react-router-dom';
import { getLocalizedText } from '../utils/textLocalization';
import {
  EditableContent,
  getModelEntryData,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
} from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const CATEGORY_TO_KEY_PREFIX = {
  gravel: 'pruvodce_gravel',
  road: 'pruvodce_road',
};

const BUILDER_LOCALE_KEY_BY_LANGUAGE = {
  cs: 'Default',
  sk: 'sk-SK',
  pl: 'pl-PL',
  hu: 'hu-HU',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const findLocalizedRecord = (contentList, contentKey, preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return null;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return null;
  }

  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    const record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      return record;
    }
  }

  return records[0] || null;
};

const getLocalizedTextValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  return fallback;
};

const getLocalizedListValue = (contentList, contentKey, preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const getLocalizedNestedValue = (value, editryLanguage) => {
  if (typeof value === 'string') {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  const localeKey = BUILDER_LOCALE_KEY_BY_LANGUAGE[editryLanguage] || 'Default';
  const directValue = value[localeKey] || value.Default;
  if (typeof directValue === 'string') {
    return directValue;
  }

  const firstKey = Object.keys(value)[0];
  const firstValue = firstKey ? value[firstKey] : '';
  return typeof firstValue === 'string' ? firstValue : '';
};

const getReferenceId = (ref) => {
  if (!ref) {
    return null;
  }

  if (typeof ref === 'number' || typeof ref === 'string') {
    return ref;
  }

  if (typeof ref === 'object') {
    return ref.id ?? ref.item_id ?? ref.value ?? null;
  }

  return null;
};

function App() {
  const { category } = useParams();
  const locale = getCurrentLanguage();
  const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
  const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
  const editryReady = isEditryConfigured();
  const normalizedCategory = (category || '').toLowerCase();
  const contentPrefix = CATEGORY_TO_KEY_PREFIX[normalizedCategory];
  const { client, overlayEnabled, adminSession } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);

  usePreloadPublicData({ auto: editryReady });
  const contentListResponse = usePublicContentList();
  const [bikeData, setBikeData] = useState({});
  const [bikeLoading, setBikeLoading] = useState(false);

  const allContent = useMemo(
    () => contentListResponse.data?.data || [],
    [contentListResponse.data]
  );

  const data = useMemo(() => {
    if (!contentPrefix) {
      return null;
    }

    return {
      nadpis: getLocalizedTextValue(allContent, `${contentPrefix}_nadpis`, '', preferredLanguages),
      popis: getLocalizedTextValue(allContent, `${contentPrefix}_popis`, '', preferredLanguages),
      obrazek: getLocalizedTextValue(allContent, `${contentPrefix}_obrazek`, '', preferredLanguages),
      sekce: getLocalizedListValue(allContent, `${contentPrefix}_sekce`, preferredLanguages),
    };
  }, [allContent, contentPrefix, preferredLanguages]);

  const sekceFallbackJson = useMemo(
    () => JSON.stringify(data?.sekce || [], null, 2),
    [data]
  );

  const buttonText = getLocalizedText('pruvodce.button');
  const isPageLoading = editryReady && contentListResponse.loading && !allContent.length;
  const hasUnknownCategory = !contentPrefix;

  useEffect(() => {
    if (!editryReady || !contentPrefix) {
      setBikeData({});
      return;
    }

    let cancelled = false;

    const loadBikes = async () => {
      setBikeLoading(true);
      try {
        const modelsResponse = await client.getPublicModels();
        const bikeModel = modelsResponse?.data?.find((model) => model.slug === 'kola');

        if (!bikeModel) {
          if (!cancelled) {
            setBikeData({});
          }
          return;
        }

        const itemsResponse = await client.getPublicModelItems(bikeModel.id, editryLanguage);
        const map = {};

        (itemsResponse?.data || []).forEach((item) => {
          map[item.id] = item;
          if (item.slug) {
            map[item.slug] = item;
          }
        });

        if (!cancelled) {
          setBikeData(map);
        }
      } catch (error) {
        if (!cancelled) {
          setBikeData({});
        }
      } finally {
        if (!cancelled) {
          setBikeLoading(false);
        }
      }
    };

    loadBikes();

    return () => {
      cancelled = true;
    };
  }, [client, contentPrefix, editryLanguage, editryReady]);

  useEffect(() => {
    document.title = getLocalizedText('pruvodce.page_title');
  }, []);

  const renderEditableText = (contentKey, fallback = '') => {
    if (!isEditModeActive) {
      return fallback;
    }

    return (
      <EditableContent
        contentKey={contentKey}
        language={editryLanguage}
        as="span"
        fallback={fallback}
        showFallbackWhileLoading={false}
      />
    );
  };

  if (isPageLoading || bikeLoading) {
    return (
      <motion.div className="page" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div className="pruvodce-page-blocker">
          <img src={image} alt="Nukeproof Gravel Bike" />
          <h1>Načítání...</h1>
          <h2>Připravujeme pro vás průvodce</h2>
        </div>
      </motion.div>
    );
  }

  if (hasUnknownCategory) {
    return (
      <motion.div className="page" key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div className="pruvodce-page-blocker">
          <img src={image} alt="Nukeproof Gravel Bike" />
          <h1>Chyba</h1>
          <h2>Průvodce pro kategorii "{category}" nebyl nalezen</h2>
        </div>
      </motion.div>
    );
  }

  if (!data) {
    return (
      <motion.div className="page" key="no-data" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div className="pruvodce-page-blocker">
          <img src={image} alt="Nukeproof Gravel Bike" />
          <h1>Průvodce není dostupný</h1>
          <h2>Data se nepodařilo načíst</h2>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div className="page" key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div className="pruvodce-page-blocker">
          {isEditModeActive ? (
            <EditableContent
              contentKey={`${contentPrefix}_obrazek`}
              language={editryLanguage}
              as="img"
              contentType="file"
              fallback={data.obrazek || image}
              className="pruvodce-hero-image-editable"
              showFallbackWhileLoading={false}
            />
          ) : (
            <img
              src={data.obrazek ? `${data.obrazek}?format=webp` : image}
              alt="Nukeproof Gravel Bike"
            />
          )}
          <div className="page-background-pruvodce-div" />
          <h1>{renderEditableText(`${contentPrefix}_nadpis`, data.nadpis || 'Průvodce kol Gravel')}</h1>
          <h2>{renderEditableText(`${contentPrefix}_popis`, data.popis || 'Vše, co potřebujete vědět o kolech Gravel')}</h2>
        </div>
        <div className="pruvodce-page-content">
          {isEditModeActive && (
            <div className="pruvodce-page-item" style={{ marginBottom: 20 }}>
              <h1>Sekce (JSON)</h1>
              <EditableContent
                contentKey={`${contentPrefix}_sekce`}
                language={editryLanguage}
                as="pre"
                className="info-content"
                contentType="list"
                fallback={sekceFallbackJson}
                showFallbackWhileLoading={false}
              />
            </div>
          )}

          {Array.isArray(data.sekce) && data.sekce.map((sekce, index) => (
            <div className="pruvodce-page-item" key={index}>
              <h1>{getLocalizedNestedValue(sekce?.nadpis, editryLanguage)}</h1>
              <div dangerouslySetInnerHTML={{ __html: getLocalizedNestedValue(sekce?.popis, editryLanguage) }} />
              {/* <img src={"https://highland-sport.cz/cdn/shop/collections/gravel.jpg?crop=center&height=600&v=1753975200&width=1200"} alt="Obrázek" className='pruvodce-sekce-image' /> */}
              {sekce?.obrazek && <img src={`${sekce.obrazek}?format=webp`} alt="Obrázek" className='pruvodce-sekce-image'/> }
              
              {Array.isArray(sekce?.kola) && sekce.kola.map((koloItem, koloIndex) => {
                const koloRef = koloItem?.kolo || koloItem;
                const koloId = getReferenceId(koloRef);
                const koloSlug = typeof koloRef === 'object' ? koloRef?.slug : null;

                if (!koloId && !koloSlug) {
                  return null;
                }
                
                const bike = bikeData[koloId] || bikeData[koloSlug];
                const bikeEntryData = getModelEntryData(bike);

                if (!bike && typeof koloItem !== 'object') {
                  return null;
                }

                const nazev =
                  getLocalizedNestedValue(bikeEntryData?.nazev, editryLanguage) ||
                  getLocalizedNestedValue(koloItem?.nazev, editryLanguage) ||
                  bike?.name ||
                  '';
                const oProduktu =
                  getLocalizedNestedValue(bikeEntryData?.oProduktu, editryLanguage) ||
                  getLocalizedNestedValue(koloItem?.oProduktu, editryLanguage) ||
                  getLocalizedNestedValue(koloItem?.popis, editryLanguage) ||
                  '';
                const obrazek =
                  getLocalizedNestedValue(bikeEntryData?.obrazek, editryLanguage) ||
                  getLocalizedNestedValue(koloItem?.obrazek, editryLanguage) ||
                  '';
                const detailId = bike?.id || koloId;
                
                return (
                  <div className="pruvodce-page-bike-content" key={koloIndex}>
                    <div>
                      <img 
                        src={obrazek || kolo} 
                        alt={nazev || "Nukeproof Bike Image"} 
                      />
                    </div>
                    <h2>{nazev || 'Bike name'}</h2>
                    <h3>{oProduktu || 'Popis produktu není dostupný'}</h3>
                    <NavLink to={`/kola/${normalizedCategory}/${detailId}`}>
                      <button><span>{buttonText}</span></button>
                    </NavLink>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </motion.div>
    </>
  );
}

export default App;